import Konva from 'konva';
import {
    GRAPH_HEADER_HEIGHT, GRAPH_PORT_RADIUS, GRAPH_WIRE_HIT_WIDTH, nodeGeometry, objectPortPoint, objectWirePoint, wireBezierPoints,
} from './graphGeometry.js';

/**
 * Konva shapes carry these attrs so interaction (hit testing, selection,
 * wire drawing) can identify what was hit without a lookup table.
 */
export const GRAPH_ATTR = Object.freeze({
    node: 'graphNodeId',
    wire: 'graphWireId',
    object: 'graphObjectUuid',
    port: 'graphPort',
    portDir: 'graphPortDir',
});

function severityOf(diagnostics, nodeId) {
    const own = diagnostics.filter(item => item.nodeId === nodeId);
    if (own.some(item => item.severity === 'error')) return 'error';
    return own.length > 0 ? 'warning' : null;
}

/**
 * Build the graph overlay of one page as a Konva group.
 *
 * @param {object} scene
 * @param {object} scene.domain       render domain: { namePrefix, getNodeSchema, nodeSize,
 *                                    describeNode(node) → { icon, title, summary, color, outputLabels? },
 *                                    colors, wireColor?(wire) }
 * @param {{x,y}} scene.origin        page origin in layer coordinates
 * @param {object} scene.graph        the page's graph
 * @param {Array}  scene.objects      [{ uuid, rect, ports: [{ id, dir: 'in'|'out', side: 'left'|'right' }] }]
 *                                    UI objects in layer coordinates
 * @param {object} [scene.selection]  { kind: 'node'|'wire', id }
 * @param {Array}  [scene.diagnostics] the domain's validation output
 * @param {object} [scene.pendingWire] wire being drawn: { from, to, pointer, valid: true|false|null, reason? }
 */
export function buildGraphScene({ domain, origin, graph, objects, selection = null, diagnostics = [], pendingWire = null }) {
    const colors = domain.colors;
    const name = suffix => `${domain.namePrefix}-${suffix}`;
    const root = new Konva.Group({ name: name('scene') });
    const objectsByUuid = new Map(objects.map(object => [object.uuid, object]));
    const nodes = new Map(graph.nodes.map(node => [node.id, node]));
    const isSelected = (kind, id) => selection?.kind === kind && selection.id === id;

    const portCircle = (point, attrs, fill = colors.port) => new Konva.Circle({
        x: point.x, y: point.y, radius: GRAPH_PORT_RADIUS,
        fill, stroke: colors.portStroke, strokeWidth: 1,
        name: name('port'), ...attrs,
    });

    // Where a wire end attaches: `dir` is 'out' for its start, 'in' for its end.
    const endPoint = (endpoint, dir) => {
        if (endpoint.object !== undefined) {
            const object = objectsByUuid.get(endpoint.object);
            return object ? objectWirePoint(object, endpoint.port, dir) : null;
        }
        const node = nodes.get(endpoint.node);
        return node ? nodeGeometry(node, origin, domain).point(dir, endpoint.port) : null;
    };

    const wires = [...graph.wires].sort((a, b) => Number(isSelected('wire', a.id)) - Number(isSelected('wire', b.id)));
    for (const wire of wires) {
        const from = endPoint(wire.from, 'out');
        const to = endPoint(wire.to, 'in');
        if (!from || !to) continue;
        const selected = isSelected('wire', wire.id);
        root.add(new Konva.Line({
            points: wireBezierPoints(from, to),
            bezier: true,
            stroke: selected ? colors.selected : domain.wireColor?.(wire) ?? colors.wire,
            strokeWidth: selected ? 3 : 2,
            hitStrokeWidth: GRAPH_WIRE_HIT_WIDTH,
            lineCap: 'round',
            name: name('wire'),
            [GRAPH_ATTR.wire]: wire.id,
        }));
    }

    // Object ports go above the wires: a wire starts on its port, and its wide
    // hit stroke would otherwise take the press meant for a new wire.
    for (const object of objects) {
        for (const port of object.ports ?? []) {
            root.add(portCircle(objectPortPoint(object.rect, port.side), {
                [GRAPH_ATTR.object]: object.uuid, [GRAPH_ATTR.port]: port.id, [GRAPH_ATTR.portDir]: port.dir,
            }, colors.objectPort));
        }
    }

    for (const node of graph.nodes) {
        const geometry = nodeGeometry(node, origin, domain);
        const { width, height } = geometry.rect;
        const view = domain.describeNode(node);
        const selected = isSelected('node', node.id);
        const severity = severityOf(diagnostics, node.id);
        const group = new Konva.Group({ x: geometry.rect.x, y: geometry.rect.y, name: name('node'), [GRAPH_ATTR.node]: node.id });
        group.add(new Konva.Rect({
            width, height, cornerRadius: 6,
            fill: colors.body, stroke: selected ? colors.selected : severity ? colors[severity] : view.color,
            strokeWidth: selected ? 2 : 1,
        }));
        group.add(new Konva.Rect({ width, height: GRAPH_HEADER_HEIGHT, cornerRadius: [6, 6, 0, 0], fill: view.color }));
        group.add(new Konva.Text({
            x: 8, y: 3, width: width - 16, text: `${view.icon} ${view.title}`,
            fontSize: 11, fontStyle: 'bold', fill: colors.text, wrap: 'none', ellipsis: true,
        }));
        // Labelled outputs (e.g. switch cases) take the right half of the body.
        group.add(new Konva.Text({
            x: 8, y: GRAPH_HEADER_HEIGHT + 7, width: (view.outputLabels ? width / 2 : width) - 16, text: view.summary,
            fontSize: 11, fill: colors.summary, wrap: 'none', ellipsis: true,
        }));
        const atLocal = point => ({ x: point.x - geometry.rect.x, y: point.y - geometry.rect.y });
        for (const port of view.outputLabels ? geometry.ports.outputs ?? [] : []) {
            group.add(new Konva.Text({
                x: width / 2, y: atLocal(geometry.point('out', port.id)).y - 5, width: width / 2 - 10, text: port.label,
                fontSize: 10, fill: colors.summary, align: 'right', wrap: 'none', ellipsis: true, listening: false,
            }));
        }
        for (const [list, dir] of [[geometry.ports.inputs, 'in'], [geometry.ports.outputs, 'out']]) {
            for (const port of list ?? []) {
                group.add(portCircle(atLocal(geometry.point(dir, port.id)), {
                    [GRAPH_ATTR.node]: node.id, [GRAPH_ATTR.port]: port.id, [GRAPH_ATTR.portDir]: dir,
                }));
            }
        }
        root.add(group);
    }

    if (pendingWire) {
        const color = pendingWire.valid === true ? colors.valid
            : pendingWire.valid === false ? colors.error : colors.portStroke;
        root.add(new Konva.Line({
            points: wireBezierPoints(pendingWire.from, pendingWire.to),
            bezier: true, stroke: color, strokeWidth: 2, dash: [6, 4], lineCap: 'round',
            listening: false, name: name('pending-wire'),
        }));
        if (pendingWire.valid === false && pendingWire.reason) {
            root.add(buildGraphTip(pendingWire.pointer, pendingWire.reason, colors, colors.error));
        }
    }
    return root;
}

// Longer tips wrap at this width (layer px).
const TIP_MAX_WIDTH = 240;

/** A small text box below and right of `point` (layer coordinates): why a wire is refused, what a port is for. */
export function buildGraphTip(point, text, colors, stroke = colors.portStroke) {
    const content = new Konva.Text({ text, fontSize: 11, padding: 4, fill: colors.text });
    if (content.width() > TIP_MAX_WIDTH) content.width(TIP_MAX_WIDTH);
    return new Konva.Label({ x: point.x + 12, y: point.y + 12, listening: false })
        .add(new Konva.Tag({ fill: colors.body, stroke, cornerRadius: 4 }))
        .add(content);
}
